'use client';

import { text, vstack } from '@onbeam/business-styled-system/patterns';
import { Link, SuperstateButton } from '@onbeam/business-ui';
import { useEffect, useState } from 'react';
import { ApplicationFormSchema } from '../ApplicationForm.utils';
import { useFormContext } from '@/components/features/Form/FormContext';
import { FormReviewGroup } from '@/components/features/Form/FormReviewGroup';
import { DrawerField } from '@/components/shared/DrawerField';
import { MarkdownRenderer } from '@/components/shared/MarkdownRenderer';
import { css } from '@onbeam/business-styled-system/css';
import {
  applicationProgressReportingFrequencyMap,
  applicationTeamTypeMap,
} from '../../ApplicationsList/ApplicationsList.types';
import { format } from 'date-fns';
import { applicationStyleMap } from '../../ApplicationsList/ApplicationsList.data';
import { formatUsd } from '@onbeam/utils';

export const ApplicationFormReview = () => {
  const {
    form: {
      getValues,
      formState: { isSubmitting },
    },
  } = useFormContext<ApplicationFormSchema>();
  const [data, setData] = useState<ApplicationFormSchema>();

  useEffect(() => {
    setData(getValues());
  }, [getValues]);

  if (!data) return null;

  return (
    <div className={vstack({ gap: '10' })}>
      <h1 className={text({ style: 'subtitle-md' })}>Review & submit</h1>
      <FormReviewGroup title="Project Details" stepIndex={0}>
        <DrawerField title="Project title" description={data.name} />
        <DrawerField
          title="Description"
          description={<MarkdownRenderer>{data.description}</MarkdownRenderer>}
        />
      </FormReviewGroup>
      <hr className={css({ w: 'full', color: 'opacity.md' })} />
      <FormReviewGroup title="Introduce your team" stepIndex={1}>
        <DrawerField title="Applicant name" description={data.applicant} />
        <DrawerField
          title="Team type"
          description={applicationTeamTypeMap[data.teamType]}
        />
        <DrawerField
          title="Team members"
          description={
            <MarkdownRenderer>{data.teamDescription}</MarkdownRenderer>
          }
        />
      </FormReviewGroup>
      <hr className={css({ w: 'full', color: 'opacity.md' })} />
      <FormReviewGroup title="Funding & roadmap" stepIndex={2}>
        <DrawerField
          title="Total funding requested"
          description={formatUsd(data.fundingRequest)}
        />
        {data.detailedBudget && (
          <DrawerField
            title="Detailed budget"
            description={
              <MarkdownRenderer>{data.detailedBudget}</MarkdownRenderer>
            }
          />
        )}
        <DrawerField
          title="Expected timeline"
          description={
            <ul>
              <li>Start: {format(data.dateRange.from, 'MMMM yyyy')}</li>
              <li>
                Expected completion: {format(data.dateRange.to, 'MMMM yyyy')}
              </li>
            </ul>
          }
        />
        <DrawerField
          title="Milestones"
          description={<MarkdownRenderer>{data.milestones}</MarkdownRenderer>}
        />
      </FormReviewGroup>
      <hr className={css({ w: 'full', color: 'opacity.md' })} />
      <FormReviewGroup
        title="Why Beam Foundation should fund you"
        stepIndex={3}
      >
        <DrawerField
          title="Rationale"
          description={<MarkdownRenderer>{data.rationale}</MarkdownRenderer>}
        />
        <DrawerField
          title="Integration with Beam"
          description={data.categories
            .map((category) => applicationStyleMap[category].name)
            .join(', ')}
        />
        <DrawerField
          title="Progress Reporting"
          description={
            applicationProgressReportingFrequencyMap[
              data.progressReportingFrequency
            ]
          }
        />
      </FormReviewGroup>
      {data.demoURL && (
        <>
          <hr className={css({ w: 'full', color: 'opacity.md' })} />
          <FormReviewGroup title="Extra details" stepIndex={4}>
            <DrawerField
              title="MVP Link"
              description={
                <Link
                  href={data.demoURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="blue"
                >
                  {data.demoURL}
                </Link>
              }
            />
          </FormReviewGroup>
        </>
      )}
      <SuperstateButton
        type="submit"
        buttonClassName={css({ w: 'full' })}
        isLoading={isSubmitting}
      >
        {!isSubmitting && 'Submit'}
      </SuperstateButton>
    </div>
  );
};
